import React from "react";

// reactstrap components
import {
  Container
} from "reactstrap";

// core components
import MainNavbar from "components/Navbars/MainNavbar.js";
import LandingPageHeader from "components/Headers/LandingPageHeader.js";
import DefaultFooter from "components/Footers/DefaultFooter.js";
import SEO from "components/SEO/SEO.js";
import accomplishmentYears from "data/accomplishments.js";

function getDogNames(names) {
  return names.split(",").map(name => name.trim()).filter(Boolean);
}

function getDogAccomplishmentId(name) {
  return `accomplishments-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
}

function getYearAccomplishmentId(year) {
  return `accomplishments-${year}`;
}

function getDogAccomplishmentsForYear(entries) {
  const dogAccomplishmentGroups = entries.reduce((groups, entry) => {
    getDogNames(entry.names).forEach(name => {
      if (!groups[name]) {
        groups[name] = [];
      }

      groups[name].push(entry);
    });

    return groups;
  }, {});

  return Object.entries(dogAccomplishmentGroups).map(([name, accomplishments]) => ({
    name,
    accomplishments
  }));
}

function getSportAccomplishments(accomplishments) {
  return accomplishments.reduce((sports, accomplishment) => {
    const sportName = accomplishment.sport || "Highlights";
    const existingSport = sports.find(sport => sport.name === sportName);

    if (existingSport) {
      existingSport.accomplishments.push(accomplishment);
      return sports;
    }

    sports.push({
      name: sportName,
      icon: accomplishment.icon,
      accomplishments: [accomplishment]
    });

    return sports;
  }, []);
}

function getDogAccomplishmentColumns(dogAccomplishments, columnCount = 3) {
  const itemsPerColumn = Math.ceil(dogAccomplishments.length / columnCount);

  return Array.from({ length: columnCount }, (_, index) =>
    dogAccomplishments.slice(index * itemsPerColumn, (index + 1) * itemsPerColumn)
  ).filter(column => column.length > 0);
}

function DogAccomplishmentGroup({ name, accomplishments, year }) {
  const headingId = `${getYearAccomplishmentId(year)}-${getDogAccomplishmentId(name)}`;
  const sportAccomplishments = getSportAccomplishments(accomplishments);

  return (
    <section className="about-dog-accomplishment-group" aria-labelledby={headingId}>
      <div className="about-dog-accomplishment-heading">
        <h3 id={headingId}>{name}</h3>
      </div>
      <div className="about-dog-accomplishment-sports">
        {sportAccomplishments.map(sport => (
          <div className="about-dog-sport-group" key={`${name}-${year}-${sport.name}`}>
            <h4 className="about-dog-accomplishment-meta">
              {sport.icon && <img alt="" aria-hidden="true" src={sport.icon} />}
              {sport.name}
            </h4>
            <ul className="about-dog-accomplishment-list">
              {sport.accomplishments.map(accomplishment => (
                <li key={`${name}-${year}-${sport.name}-${accomplishment.text}`}>
                  <span className="about-dog-accomplishment-text">{accomplishment.text}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

function AccomplishmentYear({ year, entries }) {
  const dogAccomplishments = getDogAccomplishmentsForYear(entries);
  const dogAccomplishmentColumns = getDogAccomplishmentColumns(dogAccomplishments);
  const headingId = getYearAccomplishmentId(year);

  return (
    <section className="about-accomplishment-year" aria-labelledby={headingId}>
      <div className="about-accomplishment-year-heading">
        <h3 id={headingId}>{year}</h3>
        <span>{entries.length} highlights</span>
      </div>
      <div className="about-dog-accomplishment-columns">
        {dogAccomplishmentColumns.map((column, columnIndex) => (
          <div className="about-dog-accomplishment-column" key={`${year}-column-${columnIndex}`}>
            {column.map(dog => (
              <DogAccomplishmentGroup
                key={`${year}-${dog.name}`}
                name={dog.name}
                accomplishments={dog.accomplishments}
                year={year}
              />
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

function AboutPage() {
  React.useEffect(() => {
    document.body.classList.add("landing-page");
    document.body.classList.add("sidebar-collapse");
    document.documentElement.classList.remove("nav-open");
    return function cleanup() {
      document.body.classList.remove("landing-page");
      document.body.classList.remove("sidebar-collapse");
    };
  });
  return (
      <>
        <SEO
          title="About"
          description="Learn about Endgame Sport Dogs, Adriana Nottestad, and the sport dog breeding program focused on health, temperament, and performance near Raleigh, NC."
          path="/about"
        />
        <MainNavbar />
        <div className="wrapper about-page site-bg-light-grey">
          <LandingPageHeader
            bg="header2"
            hideText
            title="About Endgame Sport Dogs"
            subtitle="A small North Carolina program focused on healthy, versatile sport dogs."
          />
          <main className="about-page-main site-bg-light-grey">
            <section className="about-program-section">
              <Container>
                <div className="about-program-grid">
                  <div className="about-program-copy">
                    <p className="about-eyebrow">About the Program</p>
                    <h2>From Competitor to Breeder</h2>
                    <span className="about-heading-line" aria-hidden="true"></span>
                    <p>
                      Hi, I'm Adriana. Since 2013, I've trained and competed in a wide range of dog sports, earning titles in agility, dock diving, flyball, disc, rally, barn hunt, and more. My first well-bred Border Collie, Gambit, made four World Teams, earned more than 30 national agility podium finishes, and competed successfully across seven different sports. Training and competing with a dog capable of excelling in so many venues made me consider trying to produce similar dogs that are versatile, have a sound temperament, and are the result of thoughtful breeding. My experience competing at the national and international levels has shaped the vision behind Endgame Sport Dogs.
                    </p>
                    <p>
                      Over the years, I've handled dogs with different strengths, challenges, and training styles while working alongside competitors from a wide variety of disciplines. Those experiences have given me a practical understanding of what makes a dog enjoyable to live with, rewarding to train, and capable of succeeding in driven sport homes.
                    </p>
                    <p>
                      That perspective guides every breeding. Endgame aims to produce healthy, versatile dogs with the temperament, athleticism, and structure to excel across multiple sports while remaining thoughtful, stable partners away from the competition field. While every dog may not be perfect and hit every goal, we strive for improvement, honesty, and thoughtful discussion regarding breeding decisions. 
                    </p>
                  </div>
                  <div className="about-program-media">
                    <img
                      alt="Adriana Nottestad with an Endgame sport dog"
                      src={require("assets/img/about3.jpg")}
                    />
                  </div>
                </div>
              </Container>
            </section>

            <section className="about-accomplishments-section site-bg-light-grey">
              <Container>
                <div className="about-accomplishments-header">
                  <h2>Endgame Dog Accomplishments</h2>
                  <span className="about-heading-line" aria-hidden="true"></span>
                  <p>
                    A list of some achievements from Endgame dogs and their offspring. We are so proud of everything our dogs and their owners have accomplished both in and outside of the ring!
                  </p>
                </div>
                <div className="about-accomplishment-years">
                  {accomplishmentYears.map(accomplishmentYear => (
                    <AccomplishmentYear
                      key={accomplishmentYear.year}
                      year={accomplishmentYear.year}
                      entries={accomplishmentYear.entries}
                    />
                  ))}
                </div>
              </Container>
            </section>
          </main>
          <DefaultFooter />
        </div>
      </>
  );
}

export default AboutPage;
